/**
 * Premium sound engine — pure Web Audio API synthesis, zero external files.
 * All sounds are designed for a professional mobile camera app feel.
 */

let _ac = null;   // AudioContext
let _master = null; // GainNode → compressor → destination

// ─── bootstrap ──────────────────────────────────────────────────────────────

function getAC() {
  if (_ac) {
    if (_ac.state === "suspended") _ac.resume().catch(() => {});
    return _ac;
  }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ac = new AC();

  // Dynamics compressor keeps every sound at consistent perceived loudness
  const comp = _ac.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.knee.value      = 8;
  comp.ratio.value     = 3;
  comp.attack.value    = 0.001;
  comp.release.value   = 0.08;
  comp.connect(_ac.destination);

  _master = _ac.createGain();
  _master.gain.value = 0.58;
  _master.connect(comp);

  return _ac;
}

// ─── primitive: schedule one oscillator tone ─────────────────────────────────
//
// Options:
//   freq       – constant frequency (Hz)
//   startFreq  – frequency at start of ramp
//   endFreq    – frequency at end of ramp (creates sweep / chirp)
//   type       – OscillatorType ('sine' | 'triangle' | 'sawtooth' | 'square')
//   vol        – peak gain
//   attack     – time to reach peak (s)
//   decay      – time from peak to near-zero (s)
//   at         – AudioContext time to start (defaults to now)
//   dur        – total duration override (s)

function tone(ac, { freq, startFreq, endFreq, type = "sine", vol = 0.24,
                    attack = 0.003, decay = 0.07, at, dur }) {
  const t = at ?? ac.currentTime;
  const d = dur ?? (attack + decay + 0.012);

  const o = ac.createOscillator();
  const g = ac.createGain();

  o.type = type;
  o.frequency.setValueAtTime(startFreq ?? freq, t);
  if (endFreq) {
    o.frequency.exponentialRampToValueAtTime(endFreq, t + d * 0.9);
  }

  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);

  o.connect(g);
  g.connect(_master);
  o.start(t);
  o.stop(t + d);
}

// ─── sound designs ──────────────────────────────────────────────────────────

/** Default tap — clean two-layer click, soft and precise */
function snd_tap(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 630,  vol: 0.21, attack: 0.002, decay: 0.055, at: t });
  tone(ac, { freq: 1260, type: "triangle", vol: 0.055, attack: 0.002, decay: 0.030, at: t });
}

/** Camera shutter — four-layer mechanical snap */
function snd_capture(ac) {
  const t = ac.currentTime;
  // Sharp click transient
  tone(ac, { freq: 980,  vol: 0.32, attack: 0.001, decay: 0.020, at: t });
  // Low body thud: bass sweep down
  tone(ac, { startFreq: 145, endFreq: 52, vol: 0.44, attack: 0.002, decay: 0.11, at: t, dur: 0.13 });
  // High-end shimmer
  tone(ac, { freq: 3400, vol: 0.040, attack: 0.002, decay: 0.022, at: t });
  // Subtle mechanical texture
  tone(ac, { freq: 240, type: "sawtooth", vol: 0.024, attack: 0.001, decay: 0.013, at: t });
}

/** Confirm / primary action — warm three-note ascending arpeggio (C5 → E5 → G5) */
function snd_confirm(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 523, vol: 0.20, attack: 0.003, decay: 0.090, at: t });
  tone(ac, { freq: 659, vol: 0.18, attack: 0.003, decay: 0.095, at: t + 0.055 });
  tone(ac, { freq: 784, vol: 0.14, attack: 0.003, decay: 0.090, at: t + 0.105 });
}

/** Toggle ON — short upward frequency sweep, airy and positive */
function snd_toggle_on(ac) {
  const t = ac.currentTime;
  tone(ac, { startFreq: 370, endFreq: 580, type: "triangle",
             vol: 0.20, attack: 0.004, decay: 0.075, at: t, dur: 0.09 });
}

/** Toggle OFF — short downward sweep, subtle dismissal */
function snd_toggle_off(ac) {
  const t = ac.currentTime;
  tone(ac, { startFreq: 520, endFreq: 350, vol: 0.16, attack: 0.003, decay: 0.065, at: t, dur: 0.08 });
}

/** Navigation tap — clean two-layer tick, bright and fast */
function snd_nav(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 840,  vol: 0.19, attack: 0.002, decay: 0.040, at: t });
  tone(ac, { freq: 1260, vol: 0.055, attack: 0.002, decay: 0.025, at: t });
}

/** Filter / selection — bell-like musical ping (C6 + G6 harmonic) */
function snd_filter(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 1047, vol: 0.17, attack: 0.001, decay: 0.100, at: t });
  tone(ac, { freq: 1568, vol: 0.065, attack: 0.001, decay: 0.070, at: t });
}

/** Zoom level change — quick ascending chirp */
function snd_zoom(ac) {
  const t = ac.currentTime;
  tone(ac, { startFreq: 560, endFreq: 880, vol: 0.17, attack: 0.003, decay: 0.050, at: t, dur: 0.07 });
}

/** Delete / danger — low descending rumble, two-part */
function snd_delete(ac) {
  const t = ac.currentTime;
  tone(ac, { startFreq: 235, endFreq: 145, type: "triangle",
             vol: 0.23, attack: 0.002, decay: 0.092, at: t, dur: 0.11 });
  tone(ac, { freq: 180, vol: 0.10, attack: 0.003, decay: 0.065, at: t + 0.025 });
}

/** Error — two descending pulses, slightly buzzy */
function snd_error(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 220, type: "triangle", vol: 0.23, attack: 0.001, decay: 0.044, at: t });
  tone(ac, { freq: 196, type: "triangle", vol: 0.21, attack: 0.001, decay: 0.044, at: t + 0.068 });
}

/** Soft / subtle — barely-there single tone for minor interactions */
function snd_soft(ac) {
  const t = ac.currentTime;
  tone(ac, { freq: 460, vol: 0.11, attack: 0.003, decay: 0.038, at: t });
}

/** EV / slider nudge — very short upward tick */
function snd_nudge(ac) {
  const t = ac.currentTime;
  tone(ac, { startFreq: 680, endFreq: 760, vol: 0.12, attack: 0.002, decay: 0.030, at: t, dur: 0.05 });
}

// ─── registry ────────────────────────────────────────────────────────────────

const SOUNDS = {
  tap:        snd_tap,
  capture:    snd_capture,
  confirm:    snd_confirm,
  "toggle-on":  snd_toggle_on,
  "toggle-off": snd_toggle_off,
  nav:        snd_nav,
  filter:     snd_filter,
  zoom:       snd_zoom,
  delete:     snd_delete,
  error:      snd_error,
  soft:       snd_soft,
  nudge:      snd_nudge,
};

// ─── public API ──────────────────────────────────────────────────────────────

export function playButtonSound(kind = "tap") {
  const ac = getAC();
  if (!ac) return;
  const fn = SOUNDS[kind] ?? SOUNDS.tap;
  try { fn(ac); } catch (_) { /* ignore audio errors silently */ }
}

// ─── auto-detect which sound a button should play ────────────────────────────

const SELECTION_CONTAINERS =
  ".mode-strip, .ai-category-tabs, .accent-grid, .theme-grid, " +
  ".hsl-hue-pills, .presets-panel, .camera-mode-strip, .editor-tab-bar, " +
  ".ai-feature-grid, .filter-list, .segmented, .view-toggle";

function kindFor(el) {
  if (!el) return "tap";

  // Explicit override via data attribute
  if (el.dataset?.sound) return el.dataset.sound;

  // ── Capture shutter ──
  if (el.classList.contains("capture-button") || el.closest(".capture-button"))
    return "capture";

  // ── Primary / confirm ──
  if (el.classList.contains("primary-button") || el.classList.contains("ai-run-button"))
    return "confirm";

  // ── Save / done buttons ──
  const text = (el.textContent || "").trim().toLowerCase();
  if (text === "save" || text === "done" || text === "apply") return "confirm";

  // ── Danger / delete ──
  if (el.classList.contains("danger-btn") || text.startsWith("delete") || text === "clear")
    return "delete";

  // ── Bottom navigation tabs ──
  if (el.closest(".bottom-nav")) return "nav";

  // ── Zoom pills ──
  if (el.classList.contains("zoom-pill")) return "zoom";

  // ── Selection / picker UIs (filters, modes, tabs, themes…) ──
  if (el.closest(SELECTION_CONTAINERS) ||
      el.classList.contains("filter-chip") ||
      el.classList.contains("preset-chip") ||
      el.classList.contains("hsl-hue-pill") ||
      el.classList.contains("strip-mode-btn") ||
      el.classList.contains("editor-tab-btn") ||
      el.classList.contains("theme-card"))
    return "filter";

  // ── Toggle / rail buttons ──
  if (el.classList.contains("rail-button") ||
      el.classList.contains("flip-button") ||
      el.classList.contains("text-icon-button") ||
      el.classList.contains("pro-mini-button"))
    return "toggle-on";

  // ── EV button ──
  if (el.classList.contains("ev-btn")) return "nudge";

  // ── Ghost / cancel ──
  if (el.classList.contains("ghost-button") || el.classList.contains("ghost-link"))
    return "soft";

  return "tap";
}

// ─── install global listener ─────────────────────────────────────────────────

export function installButtonSounds(enabled = true) {
  if (!enabled) return () => {};

  const onPointerUp = (e) => {
    const target = e.target.closest?.('button, [role="button"], a, select, summary');
    if (!target || target.disabled) return;
    playButtonSound(kindFor(target));
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const target = e.target.closest?.('button, [role="button"], a, select, summary');
    if (!target || target.disabled) return;
    playButtonSound(kindFor(target));
  };

  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("keydown",   onKeyDown,   true);

  return () => {
    document.removeEventListener("pointerup", onPointerUp, true);
    document.removeEventListener("keydown",   onKeyDown,   true);
  };
}
